import { Injectable } from '@angular/core';
import { Message } from './message.model';
import { MessageQueueSearch } from './message-queue-search.model';
import { MessageDraftProfileSearch } from './message-draft-profile-search.model';
import { ApiService, ApiCacheOptions } from '../../shared/index';
import { PagedResult } from '../../shared/model/generic-paged-result.model';
import { NameValue } from '../../shared/model/name-value.model';
import { CommunicationRecord } from './communication-record.model';
import { CommunicationRecordSearch } from './communication-record-search.model';
import { ClickLogSearch } from './click-log-search.model';
import { ClickLog } from './click-log.model';
import { DraftProfileMin } from './draft-profile-min.model';
import { WebContentType } from '../../shared/model/webcontenttype.model';
import { SentMessageStatistics } from './sent-message-statistics.model';
import { ScheduleModeEnum } from './schedule-mode.enum';
import { MessageResendDto } from './message-resend.dto';

@Injectable()
export class MassCommService {
  private _getSpecialFieldsPromise: Promise<Array<NameValue>> = null;

  /**
   * inject
   * @param _apiService
   */
  public constructor(private _apiService: ApiService) {}

  /**
   * search for messages
   * @param params
   */
  public search(params: MessageQueueSearch = new MessageQueueSearch()): Promise<PagedResult<Array<Message>>> {
    params.TimeZoneOffsetMinutes = new Date().getTimezoneOffset();
    return this._apiService.getPaged('Communication/Messages', params);
  }

  public searchScheduled(params: MessageQueueSearch = new MessageQueueSearch()): Promise<PagedResult<Array<Message>>> {
    params.TimeZoneOffsetMinutes = new Date().getTimezoneOffset();
    return this._apiService.postPaged('Communication/ScheduledEmails', params);
  }

  public getMessage(id: number): Promise<Message> {
    return this._apiService.get(`Communication/Message/${id}`);
  }

  /**
   * create a message
   * @param message
   */
  public createMessage(message: Message): Promise<number> {
    return this._apiService.post('Communication/Messages', message).then((id: number) => {
      message.MessageId = id;
      return id;
    });
  }

  /**
   * update a message
   * @param message
   */
  public updateMessage(message: Message): Promise<void> {
    return this._apiService.put(`Communication/Message/${message.MessageId}`, message);
  }

  /**
   * delete a message
   * @param id
   */
  public deleteMessage(id: number): Promise<void> {
    return this._apiService.delete(`Communication/Message/${id}`);
  }

  /**
   * search for draft messages
   * @param params
   */
  public searchDrafts(params: MessageQueueSearch = new MessageQueueSearch()): Promise<PagedResult<Array<Message>>> {
    params.TimeZoneOffsetMinutes = new Date().getTimezoneOffset();
    return this._apiService.getPaged('Communication/DraftEmails', params);
  }

  public getDraftCommunicationCount(params: MessageDraftProfileSearch): Promise<number> {
    return this._apiService.post('Communication/DraftEmail/CommunicationCount', params);
  }

  /**
   * get a draft message by id
   * @param id
   */
  public getDraftMessage(id: number): Promise<Message> {
    return this._apiService.get(`Communication/DraftEmail/${id}`);
  }

  /**
   * create a draft message
   * @param draftMessage
   */
  public createDraftMessage(draftMessage: Message): Promise<number> {
    draftMessage.Recurring = draftMessage.ScheduleMode === ScheduleModeEnum.Recurring;
    return this._apiService.post('Communication/DraftEmails', draftMessage).then((id: number) => {
      draftMessage.MessageId = id;
      return id;
    });
  }

  /**
   * update a draft message
   * @param draftMessage
   */
  public updateDraftMessage(draftMessage: Message): Promise<void> {
    draftMessage.Recurring = draftMessage.ScheduleMode == ScheduleModeEnum.Recurring;
    return this._apiService.put(`Communication/DraftEmail/${draftMessage.MessageId}`, draftMessage);
  }

  /**
   * delete a draft message
   * @param id
   */
  public deleteDraftMessage(id: number): Promise<void> {
    return this._apiService.delete(`Communication/DraftEmail/${id}`);
  }

  /**
   * special fields list
   */
  public getEmailSpecialFields(): Promise<Array<NameValue>> {
    if (this._getSpecialFieldsPromise == null) {
      this._getSpecialFieldsPromise = this._apiService.get('Communication/EmailSpecialFields').then(
        (specialFields) => {
          if (!specialFields || !specialFields.map) return specialFields;

          return specialFields.map((x: any) => new NameValue(x.Name, x.Value));
        },
        (e) => {
          this._getSpecialFieldsPromise = null;
          return e;
        }
      );
    }

    return this._getSpecialFieldsPromise;
  }

  /**
   * preview a comm's message
   * @param message
   * @param profileId
   */
  public getPreviewMessageUrl(message: Message, profileId: number = 0): Promise<string> {
    // gets a url to the preview
    return this._apiService.post(`Communication/PreviewMessage/${profileId}`, message);
  }

  /**
   * send a preview a of a comm's message
   * @param message
   * @param testRiciepientEmail
   * @param profileId
   */
  public sendTestMessage(message: Message, testRiciepientEmail: string, profileId: number = 0): Promise<void> {
    let urlSafeEmail: string = encodeURIComponent(testRiciepientEmail);

    return this._apiService.post(`Communication/SendTestMessage/${profileId}?email=${urlSafeEmail}`, message);
  }

  /**
   * given the advanced search param, get the ProfileIds that have "Do not send email" turned on.
   * @param search
   */
  public getDoNotEmailProfileIds(search: MessageDraftProfileSearch): Promise<Array<number>> {
    if (
      !search ||
      ((!search.UnionProfileSearches || !search.UnionProfileSearches.length) &&
        (!search.UnionInvoiceSearches || !search.UnionInvoiceSearches.length))
    )
      return Promise.resolve([]);

    return this._apiService.post('Communication/DoNotEmailProfileIds', search);
  }

  /**
   * given the advanced search param, get the profiles that have "Do not send email" turned on.
   * @param search
   */
  public getDoNotEmailProfiles(search: MessageDraftProfileSearch): Promise<PagedResult<Array<DraftProfileMin>>> {
    if (
      !search ||
      ((!search.UnionProfileSearches || !search.UnionProfileSearches.length) &&
        (!search.UnionInvoiceSearches || !search.UnionInvoiceSearches.length))
    ) {
      let emptyResult: PagedResult<Array<DraftProfileMin>> = new PagedResult<Array<DraftProfileMin>>([]);
      emptyResult.TotalCount = 0;
      emptyResult.TotalPages = 0;
      emptyResult.CurrentPageNumber = 1;
      return Promise.resolve(emptyResult);
    }

    return this._apiService.postPaged('Communication/DoNotEmailProfiles', search);
  }

  /**
   * search for profiles for a draft message
   * @param search
   */
  public searchDraftProfiles(search: MessageDraftProfileSearch): Promise<PagedResult<Array<DraftProfileMin>>> {
    if (
      !search ||
      ((!search.UnionProfileSearches || !search.UnionProfileSearches.length) &&
        (!search.UnionInvoiceSearches || !search.UnionInvoiceSearches.length))
    ) {
      let emptyResult: PagedResult<Array<DraftProfileMin>> = new PagedResult<Array<DraftProfileMin>>([]);
      emptyResult.TotalCount = 0;
      emptyResult.TotalPages = 0;
      emptyResult.CurrentPageNumber = 1;
      return Promise.resolve(emptyResult);
    }

    return this._apiService.postPaged('Communication/DraftEmail/Profiles', search);
  }

  /**
   * export profiles for a draft message to excel.
   * @param message
   * @returns url to excel file
   */
  public exportSearchDraftsAsExcel(message: Message): Promise<string> {
    return this._apiService.post('Communication/DraftEmail/Profiles/Export/Excel', message);
  }

  /**
   * search for communication records
   * @param search
   */
  public searchCommunicationRecords(search: CommunicationRecordSearch): Promise<PagedResult<Array<CommunicationRecord>>> {
    return this._apiService.getPaged('Communication/Communications', search);
  }

  /**
   * get communication records search as excel
   * @param search
   * @returns url to excel file
   */
  public exportCommunicationRecordsAsExcel(search: CommunicationRecordSearch): Promise<string> {
    return this._apiService.getPaged('Communication/Communications/Export/Excel', search);
  }

  /**
   * search for click logs
   * @param search
   */
  public searchClickLogs(search: ClickLogSearch): Promise<PagedResult<Array<ClickLog>>> {
    return this._apiService.getPaged('Communication/ClickLogs', search);
  }

  /**
   * get click logs search as excel
   * @param search
   * @returns url to excel file
   */
  public exportClickLogsAsExcel(search: ClickLogSearch): Promise<string> {
    return this._apiService.getPaged('Communication/ClickLogs/Export/Excel', search);
  }

  /**
   * get web content types
   * @returns un-paged list of web content types
   */
  public getWebContenTypes(): Promise<WebContentType[]> {
    return this._apiService.getPaged('WebContentTypes', { PageSize: 0, PageNumber: 0 }).then((result) => {
      if (result && result.Result) return result.Result;

      return [];
    });
  }

  /**
   * send mass com
   */
  public getMessageStatistics(messageId: number): Promise<SentMessageStatistics> {
    if (messageId <= 0) return Promise.resolve(null);
    let options: ApiCacheOptions = new ApiCacheOptions();
    options.CacheSeconds = 3;
    options.DisableSlidingCache = true;
    return this._apiService.get(`Communication/Message/${messageId}/Statistics`, options);
  }

  /**
   * Get sent log/clock log statistics by contact id.
   */
  public getSentMessageStatisticsByContactId(contactId: number): Promise<SentMessageStatistics> {
    if (contactId <= 0) {
      return Promise.resolve(null);
    }

    return this._apiService.get(`Communication/Contact/${contactId}/Statistics`, this._apiService.getDefaultApiCacheOptionsForNoCache());
  }

  /**
   * send mass com
   */
  public send(message: Message): Promise<number> {
    if (!message) return Promise.resolve(0);

    return this._apiService.post(`Communication/DraftEmail/${message.MessageId}/Send`, message);
  }

  /**
   * resend a message
   * @param message
   */
  public resendMessage(message: MessageResendDto): Promise<number> {
    return this._apiService.post('Communication/ResendMessage', message).then((id: number) => {
      return id;
    });
  }
}
